'use client';

import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import Link from 'next/link';

const links = [
  {
    id: 0,
    header: 'General',
    linkList: [
      { id: 0, href: '01', txt: 'How do I find the right Green Filter for my vehicle?' },
      { id: 1, href: '02', txt: 'How are Green Filter air filters different from standard disposable air filters?' },
      { id: 2, href: '03', txt: 'What filtration level do Green Filter air filters provide?' },
      { id: 3, href: '04', txt: 'Can I get a Green Filter in a different color?' },
      { id: 4, href: '05', txt: 'Do you make custom or private-label air filters?' },
      { id: 5, href: '06', txt: 'Do Green Filter air filters come pre-oiled?' },
      { id: 6, href: '07', txt: 'Where are Green Filter air filters made?' },
      { id: 7, href: '08', txt: 'Who uses Green High Performance Air Filters?' },
      { id: 8, href: '09', txt: 'How long does a Green Filter air filter last?' },
      { id: 9, href: '10', txt: 'Where can I buy a Green High Performance Air Filter?' },
    ],
  },
  {
    id: 1,
    header: 'Filter Maintenance',
    linkList: [
      { id: 0, href: '11', txt: 'How often should I clean my Green Filter?' },
      { id: 1, href: '12', txt: 'Can I use compressed air to clean my Green Filter?' },
      { id: 2, href: '13', txt: 'Can I use another brand of cleaner or filter oil?' },
      { id: 3, href: '14', txt: 'What makes Green Filter oil different?' },
      { id: 4, href: '15', txt: 'How do I clean and re-oil a Green Filter?' },
    ],
  },
  {
    id: 2,
    header: 'Warranty & Returns',
    linkList: [
      { id: 0, href: '16', txt: 'What warranty comes with a Green Filter?' },
      { id: 1, href: '17', txt: 'I ordered the wrong filter. Can I exchange it?' },
      { id: 2, href: '18', txt: 'How do I return a Green Filter?' },
      { id: 3, href: '19', txt: 'Can I get a replacement warranty card?' },
      { id: 4, href: '20', txt: 'How do I make a warranty claim?' },
    ],
  },
];

export default function FAQsPage() {
  return (
    <>
      <Header handleClickIndex={() => {}} />
      <div className="header-gap-creator"></div>
      <Breadcrumbs items={[{ label: 'FAQs' }]} />

      <main className="container-0">
        <ContentWrapper>
          <h1 className="title-header">Frequently Asked Questions About Green Filter Air Filters</h1>
          <p className="intro">
            Find answers to common questions about Green High Performance Air Filters, including vehicle fitment, filtration, cleaning and maintenance, filter oil, warranty, returns, and custom filter options.
          </p>

          <div className="links-container">
            {links.map((link) => (
              <div key={link.id}>
                <h2 className="faqs-links">{link.header}</h2>
                <ul>
                  {link.linkList.map((item) => (
                    <li key={item.id}>
                      <a href={`#${item.href}`}>{item.txt}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div>
              <h2 className="faqs-links">
                <u>GENERAL</u>
              </h2>

              <div className="desc-container" id="01">
                <h3 className="question">How do I find the right Green Filter for my vehicle?</h3>
                <p className="answer">
                  Use the Green Filter <Link href="/shop-by-vehicle">Shop by Vehicle</Link> tool and select your year, make, model, and engine to see compatible filter applications.
                  <br />
                  If your vehicle is not listed, <Link href="/contact-us">contact Green Filter</Link> for help with fitment or custom-filter availability.
                </p>
              </div>

              <div className="desc-container" id="02">
                <h3 className="question">How are Green Filter air filters different from standard disposable air filters?</h3>
                <p className="answer">
                  Green High Performance Air Filters use a washable, reusable oiled filtration system rather than a conventional replace-and-discard maintenance cycle.
                  <br />
                  They are designed to be cleaned, re-oiled, and returned to service when properly maintained.
                  <br />
                  Green Filter also manufactures its filters in the United States using U.S.-manufactured materials.
                  <br />
                  For more information about airflow and filter design, watch the{' '}
                  <a href="https://www.youtube.com/watch?v=cY7fSPvI8bY" target="_blank" rel="noopener noreferrer">
                    Green Filter Airflow Simulator
                  </a>
                  .
                </p>
              </div>

              <div className="desc-container" id="03">
                <h3 className="question">What filtration level do Green Filter air filters provide?</h3>
                <p className="answer">
                  Green Filter states that its High Performance Air Filters are designed to trap dirt and particles as small as 5 microns.
                  <br />
                  Filtration should also be considered together with proper fitment, sealing, airflow, filter condition, and maintenance.
                </p>
              </div>

              <div className="desc-container" id="04">
                <h3 className="question">Can I get a Green Filter in a different color?</h3>
                <p className="answer">
                  Some Green Filter applications may be available through the ColorMatch program.
                  <br />
                  ColorMatch filters use white cotton filter material that can be colored using compatible colored oil kits or dye products.
                  <br />
                  Availability can vary by filter and application, so <Link href="/contact-us">contact Green Filter</Link> if you need a specific color.
                </p>
              </div>

              <div className="desc-container" id="05">
                <h3 className="question">Do you make custom or private-label air filters?</h3>
                <p className="answer">
                  Yes. Green Filter offers custom and private-label filter manufacturing for qualifying applications.
                  <br />
                  Visit the <Link href="/private-label">Custom &amp; Private Label Filters</Link> page to learn more about available options, or <Link href="/contact-us">contact Green Filter</Link> to discuss your application.
                </p>
              </div>

              <div className="desc-container" id="06">
                <h3 className="question">Do Green Filter air filters come pre-oiled?</h3>
                <p className="answer">
                  Yes. All Green High Performance Air Filters come pre-oiled from the factory, so you do not need to apply additional oil before the first installation.
                  <br />
                  After the filter is cleaned during normal maintenance, it should be re-oiled according to Green Filter&apos;s{' '}
                  <Link href="/cleaner-care">care instructions</Link>.
                </p>
              </div>

              <div className="desc-container" id="07">
                <h3 className="question">Where are Green Filter air filters made?</h3>
                <p className="answer">
                  Green High Performance Air Filters are made by hand at Green Filter&apos;s facility in Mount Braddock, Pennsylvania, using materials manufactured in the USA.
                  <br />
                  Learn more about the company on the <Link href="/about-us">About Green Filter</Link> page.
                </p>
              </div>

              <div className="desc-container" id="08">
                <h3 className="question">Who uses Green High Performance Air Filters?</h3>
                <p className="answer">
                  Green Filter products are used by everyday drivers, automotive enthusiasts, and racing applications.
                  <br />
                  For examples of Green Filter&apos;s involvement in motorsports and its company history, visit the <Link href="/about-us">About Us</Link> page.
                </p>
              </div>

              <div className="desc-container" id="09">
                <h3 className="question">How long does a Green Filter air filter last?</h3>
                <p className="answer">
                  Green High Performance Air Filters are designed for long-term reuse and are backed by a{' '}
                  <Link href="/our-guarantee">Limited Lifetime Warranty</Link> when used and maintained according to Green Filter&apos;s warranty terms.
                  <br />
                  Unlike conventional disposable filters, a reusable Green Filter is designed to be cleaned and returned to service rather than routinely discarded.
                </p>
              </div>

              <div className="desc-container" id="10">
                <h3 className="question">Where can I buy a Green High Performance Air Filter?</h3>
                <p className="answer">
                  Green High Performance Air Filters can be purchased directly through <Link href="/">GreenFilter.com</Link> and through authorized distributors.
                  <br />
                  If you are not sure which filter fits your vehicle, start with the <Link href="/shop-by-vehicle">Shop by Vehicle</Link> tool.
                </p>
              </div>
            </div>

            <div>
              <h2 className="faqs-links">
                <u>FILTER MAINTENANCE</u>
              </h2>

              <div className="desc-container" id="11">
                <h3 className="question">How often should I clean my Green Filter?</h3>
                <p className="answer">
                  Green Filter recommends visually inspecting your filter at every oil change.
                  <br />
                  Under normal driving conditions, cleaning is generally recommended around every 30,000 miles.
                  <br />
                  If you regularly drive in dusty, off-road, agricultural, construction, or other dirty environments, the filter may need to be inspected and cleaned more frequently.
                  <br />
                  For step-by-step instructions, read{' '}
                  <Link href="/blog/how-to-clean-reusable-air-filter">How to Clean a Reusable Air Filter</Link>.
                </p>
              </div>

              <div className="desc-container" id="12">
                <h3 className="question">Can I use compressed air to clean my Green Filter?</h3>
                <p className="answer">
                  No. Green Filter does not recommend using compressed air because high-pressure air may damage the filter media.
                  <br />
                  For packed dirt or debris, use a soft-bristle brush and follow the recommended cleaning procedure.
                  <br />
                  See the Green Filter <Link href="/cleaner-care">Cleaner &amp; Care</Link> guide for proper maintenance instructions.
                </p>
              </div>

              <div className="desc-container" id="13">
                <h3 className="question">Can I use another brand of cleaner or filter oil?</h3>
                <p className="answer">
                  Green Filter recommends using Green Filter USA&apos;s approved biodegradable cleaner and lightweight synthetic filter oil.
                  <br />
                  Using unapproved cleaners or oils is not recommended and may affect the filter or its warranty coverage.
                  <br />
                  Review the current Green Filter <Link href="/our-guarantee">warranty terms</Link> before using alternative maintenance products.
                </p>
              </div>

              <div className="desc-container" id="14">
                <h3 className="question">What makes Green Filter oil different?</h3>
                <p className="answer">
                  Green Filter uses a proprietary lightweight synthetic oil formulated for its reusable cotton filter media and stainless-steel mesh design.
                  <br />
                  According to Green Filter, the oil is designed to help contaminants adhere to the filtration system while allowing the cotton media to continue functioning effectively.
                  <br />
                  The formulation also contains an additive designed to interact with the cotton filtration media.
                  <br />
                  Always follow the recommended oil quantity and application process provided in the <Link href="/cleaner-care">Cleaner &amp; Care</Link> instructions.
                </p>
              </div>

              <div className="desc-container" id="15">
                <div className="answer">
                  <h3 className="question">How do I clean and re-oil a Green Filter?</h3>
                  <p>Use the Green Filter-approved cleaning process instead of a generic air-filter cleaning method.</p>
                  <p>The basic process includes:</p>
                  <ul>
                    <li>Remove loose dirt and debris.</li>
                    <li>Apply the approved Green Filter cleaner.</li>
                    <li>Rinse the filter as directed.</li>
                    <li>Allow the filter to dry properly.</li>
                    <li>Apply Green Filter oil according to the instructions.</li>
                    <li>Reinstall the filter correctly.</li>
                  </ul>
                  <p>
                    For complete instructions, read <Link href="/blog/how-to-clean-reusable-air-filter">How to Clean a Reusable Air Filter</Link> or visit{' '}
                    <Link href="/cleaner-care">Green Filter Cleaner &amp; Care</Link>.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="faqs-links">
                <u>WARRANTY &amp; RETURNS</u>
              </h2>

              <div className="desc-container" id="16">
                <h3 className="question">What warranty comes with a Green Filter?</h3>
                <p className="answer">
                  Green High Performance Air Filters are covered by a Limited Lifetime Warranty, subject to Green Filter&apos;s warranty conditions and maintenance requirements.
                  <br />
                  Review the complete terms on the Green Filter <Link href="/our-guarantee">Warranty &amp; Guarantee</Link> page.
                </p>
              </div>

              <div className="desc-container" id="17">
                <h3 className="question">I ordered the wrong filter. Can I exchange it?</h3>
                <p className="answer">
                  Yes. Qualifying filters can be exchanged.
                  <br />
                  Before returning a filter, <Link href="/contact-us">contact Green Filter</Link> and obtain an RGA number.
                  <br />
                  Then follow the instructions on the{' '}
                  <a href="/documents/gfu_filter_credit_return.pdf" target="_blank" rel="noopener noreferrer">
                    appropriate return or exchange form
                  </a>
                  .
                  <br />
                  Do not ship a filter back before receiving the required RGA number.
                </p>
                <div className="note">
                  <span className="warn">*NOTE: YOU MUST CALL US FOR AN RGA NUMBER BEFORE SENDING ANY FILTER TO US.</span> Any filter sent to us without an RGA number may not be exchanged. Please read the form carefully and make sure that all steps are followed to insure a quick exchange of your filter.
                </div>
              </div>

              <div className="desc-container" id="18">
                <h3 className="question">How do I return a Green Filter?</h3>
                <p className="answer">
                  Before returning a filter, <Link href="/contact-us">contact Green Filter</Link> to obtain the required RGA number.
                  <br />
                  Then complete the{' '}
                  <a href="/documents/gfu_filter_credit_return.pdf" target="_blank" rel="noopener noreferrer">
                    applicable return form
                  </a>{' '}
                  and follow the current shipping instructions.
                  <br />
                  Review the Green Filter <Link href="/our-guarantee">Warranty &amp; Guarantee</Link> page for the latest return information.
                </p>
                <div className="note">
                  <span className="warn">*NOTE: YOU MUST CALL US FOR AN RGA NUMBER BEFORE SENDING ANY FILTER TO US.</span> Any filter sent to us without an RGA number may not be exchanged. Please read the form carefully and make sure that all steps are followed to insure a quick exchange of your filter.
                </div>
              </div>

              <div className="desc-container" id="19">
                <h3 className="question">Can I get a replacement warranty card?</h3>
                <p className="answer">
                  Yes. If your warranty card was lost or was not included with your filter, you can obtain a replacement through Green Filter.
                  <br />
                  Follow the current registration instructions on the <Link href="/our-guarantee">Warranty &amp; Guarantee</Link> page.
                  <br />
                  Green Filter currently requires warranty registration within 30 days of purchase.
                </p>
              </div>

              <div className="desc-container" id="20">
                <h3 className="question">How do I make a warranty claim?</h3>
                <p className="answer">
                  If your filter needs to be reviewed under warranty, visit the Green Filter <Link href="/our-guarantee">Warranty &amp; Guarantee</Link> page and follow the Warranty Return instructions.
                  <br />
                  You will need to obtain the required RGA number before returning the filter.
                  <br />
                  Follow all instructions on the{' '}
                  <a href="/documents/gfu_filter_credit_return.pdf" target="_blank" rel="noopener noreferrer">
                    warranty form
                  </a>{' '}
                  before shipping the product.
                </p>
                <div className="note">
                  <span className="warn">*NOTE: YOU MUST CALL US FOR AN RGA NUMBER BEFORE SENDING ANY FILTER TO US.</span> Any filter sent to us without an RGA number may not be exchanged. Please read the form carefully and make sure that all steps are followed to insure a quick exchange of your filter.
                </div>
              </div>
            </div>

            <div>
              <h2 className="faqs-links">Still Have a Question?</h2>
              <p className="answer">
                If your question is not answered here, <Link href="/contact-us">contact Green Filter</Link> for help with vehicle fitment, filter selection, maintenance, warranty, or custom applications.
                <br />
                If you already know an OEM or competitor filter part number, you can also use the Green Filter{' '}
                <Link href="/cross-reference-brand">air filter cross-reference tool</Link>.
              </p>
            </div>
          </div>
        </ContentWrapper>
      </main>

      <Footer />
    </>
  );
}

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;

  .title-header {
    font-size: 32px;
    font-weight: bold;
    margin-top: 0;
    margin-bottom: 20px;
  }

  .intro {
    font-size: 16px;
    color: #000;
    max-width: 800px;
    margin: 15px 0 30px;
  }

  .faqs-links {
    font-size: 20px;
    font-weight: bold;
  }

  ul {
    font-size: 16px;
  }

  a {
    color: #14800d;
    font-size: 16px;
  }

  .desc-container {
    border: 1px solid #aaaaaa;
    padding: 20px 10px;
    margin: 20px 0px;
  }

  h3.question {
    font-weight: bold;
    font-style: italic;
    color: #000000;
    font-size: 16px;
    margin: 0 0 8px;
    padding: 5px 10px;
  }

  .answer {
    color: #000;
    font-size: 16px;
  }

  .note {
    padding-left: 20px;
  }

  span.warn {
    color: #ff0000;
    font-weight: bold;
  }
`;
